import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList as List } from 'react-window';

import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';

import type { Snippet } from '../types/snippet.ts';

import { SnippetPanelCreateItem } from './snippet-panel-item/SnippetPanelCreateItem.tsx';
import { useSnippetPopupPreview } from './preview/SnippetPopupPreviewContext.tsx';
import { SnippetPanelItem } from './snippet-panel-item/SnippetPanelItem.tsx';
import { SNIPPET_CARD_HEIGHT } from './snippet-panel-item/SnippetPanelCard.tsx';

const styles = cssMap({
	container: {
		flexGrow: 1,
		minHeight: '0px',
		marginInline: token('space.negative.200'),
		overflow: 'hidden',
	},
});

export const useVirtualizedListHeight = (
	containerRef: React.RefObject<HTMLDivElement | null>,
): { containerHeight: number } => {
	const [containerHeight, setContainerHeight] = useState(0);

	useEffect(() => {
		const node = containerRef.current;
		if (!node) {
			return;
		}

		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (entry) {
				setContainerHeight(entry.contentRect.height);
			}
		});

		observer.observe(node);
		return () => observer.disconnect();
	}, [containerRef]);

	return { containerHeight };
};

type SnippetPanelListProps = {
	snippets: Snippet[];
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

type SnippetPanelListItemData = SnippetPanelListProps & {
	hasCreateRow: boolean;
};

type SnippetPanelListRowProps = {
	data: SnippetPanelListItemData;
	index: number;
	style: React.CSSProperties;
};

const SnippetPanelListRow = ({
	data: { snippets, contentId, spaceId, spaceKey, hasCreateRow },
	index,
	style,
}: SnippetPanelListRowProps) => (
	<div style={style} role="listitem">
		{hasCreateRow && index === snippets.length ? (
			<SnippetPanelCreateItem contentId={contentId} spaceId={spaceId} spaceKey={spaceKey} />
		) : (
			<SnippetPanelItem
				snippet={snippets[index]}
				contentId={contentId}
				spaceId={spaceId}
				spaceKey={spaceKey}
			/>
		)}
	</div>
);

const getItemKey = (index: number, data: SnippetPanelListItemData) =>
	data.hasCreateRow && index === data.snippets.length
		? 'snippet-panel-create-row'
		: data.snippets[index].snippetId;

export const SnippetPanelList = ({
	snippets,
	contentId,
	spaceId,
	spaceKey,
}: SnippetPanelListProps): React.JSX.Element => {
	const { hidePreview } = useSnippetPopupPreview();

	const containerRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<List>(null);

	const { containerHeight } = useVirtualizedListHeight(containerRef);
	const snippetIds = useMemo(
		() =>
			snippets
				.map((snippet) => snippet.snippetId)
				.sort()
				.join(','),
		[snippets],
	);
	const scrollResetSignal = fg('platform_editor_blocks_patch_7') ? snippetIds : snippets;

	useEffect(() => {
		// Under patch 7, reset only for a different set of snippets and retain the current position
		// when insertion updates usage count (and potentially popularity order). Keep legacy array
		// reference behavior while the gate is disabled.
		listRef.current?.scrollTo(0);
	}, [scrollResetSignal]);

	const itemCount = snippets.length + (fg('platform_synced_block_patch_14') ? 0 : 1);
	const itemData = useMemo(
		() => ({
			snippets,
			contentId,
			spaceId,
			spaceKey,
			hasCreateRow: itemCount > snippets.length,
		}),
		[snippets, contentId, spaceId, spaceKey, itemCount],
	);
	// Preserve the original inline row identity while the patch is disabled for complete rollback.
	const LegacyRow = (props: SnippetPanelListRowProps) => <SnippetPanelListRow {...props} />;
	const Row = fg('platform_editor_blocks_patch_7') ? SnippetPanelListRow : LegacyRow;

	return (
		<Box xcss={styles.container} ref={containerRef} onMouseLeave={hidePreview}>
			{containerHeight > 0 && (
				<List
					ref={listRef}
					height={containerHeight}
					itemCount={itemCount}
					itemData={itemData}
					itemSize={SNIPPET_CARD_HEIGHT}
					width="100%"
					itemKey={getItemKey}
					overscanCount={3}
				>
					{Row}
				</List>
			)}
		</Box>
	);
};
